import { todayISO } from '../../utils/format';
import type { StatutoryPayment, StatutoryPaymentHistoryEntry } from '../../types';

// The billing-cycle arithmetic behind Statutory Payments, kept apart from the
// screen so the month-end and early-payment cases can be tested.

/** Months to advance the due date by, per billing cycle. */
export const CYCLE_MONTHS: Record<string, number> = {
  Monthly: 1, 'Bi-Monthly': 2, Quarterly: 3, 'Half-Yearly': 6, Yearly: 12
};

/** Bad JSON must not take the screen down — an unreadable history reads empty. */
export const parseHistory = (raw?: string): StatutoryPaymentHistoryEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * An ISO date moved by whole months, on the calendar rather than the clock:
 * the day is kept, clamped to the month's length (31 Jan + 1 -> 28 Feb, not
 * 3 Mar — `setMonth` used to overflow and every month-end bill drifted), and
 * the result is formatted from local parts so no time-zone shift can move it
 * a day.
 */
export const addMonthsISO = (iso: string, months: number): string | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return null;
  const year = Number(m[1]); const monthIndex = Number(m[2]) - 1; const day = Number(m[3]);
  const target = new Date(year, monthIndex + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`;
};

/** Rolls a due date forward by one billing cycle. */
export const advanceDueDate = (dueDate: string, frequency: string): string => {
  const months = CYCLE_MONTHS[frequency] || 1;
  return addMonthsISO(dueDate || todayISO(), months) || dueDate;
};

/**
 * The latest payment on the bill, or null. Each payment records the due date
 * it settled (`forDue`); marking a bill paid then rolls the bill's due date
 * forward, so "paid for the current cycle" is simply: the latest payment
 * settled a due date earlier than the one now showing. That holds however
 * early the payment was made and even if the due date is later edited by
 * hand. Payments recorded before `forDue` existed fall back to a two-cycle
 * window on the paid date.
 */
export const paidThisCycle = (bill: StatutoryPayment): StatutoryPaymentHistoryEntry | null => {
  const latest = parseHistory(bill.paymentHistoryJson)
    .filter((h) => h.paidDate)
    .sort((a, b) => String(b.paidDate).localeCompare(String(a.paidDate)))[0];
  if (!latest || !bill.dueDate) return null;
  if (latest.forDue) return String(latest.forDue) < String(bill.dueDate) ? latest : null;
  const months = CYCLE_MONTHS[String(bill.frequency)] || 1;
  const windowStart = addMonthsISO(bill.dueDate, -2 * months);
  return windowStart && String(latest.paidDate) >= windowStart ? latest : null;
};

